/**
 * What the scenario picker lists, and what it calls each thing.
 *
 * Publishing a schedule archives the one it replaces, so archived schedules
 * accumulate — one per publish — and a picker that lists everything becomes a
 * long list of past schedules between you and the draft you want. They are
 * history, kept, so they are hidden by default and shown on request.
 */
export interface ScenarioItem {
  scenarioId: string;
  name: string;
  status: "draft" | "published" | "archived";
  isScratch?: boolean;
  createdAt?: string | null;
}

/** The live schedule first, then newest first. */
export function sortScenarios<T extends ScenarioItem>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.status === "published") return -1;
    if (b.status === "published") return 1;
    return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
  });
}

/** The marker in front of a name. Each kind is told apart by something other than color. */
export function scenarioPrefix(s: Pick<ScenarioItem, "status" | "isScratch">): string {
  if (s.status === "published") return "● LIVE — ";
  if (s.status === "archived") return "▪ archived — ";
  return s.isScratch ? "◌ working — " : "○ draft — ";
}

/**
 * The scenarios to list. Archived ones are left out unless asked for — except
 * the one being viewed, which must stay in the list or the picker would show a
 * blank where the current schedule is.
 */
export function visibleScenarios<T extends ScenarioItem>(
  rows: readonly T[], selectedId: string, showArchived: boolean,
): T[] {
  return showArchived ? [...rows] : rows.filter((s) => s.status !== "archived" || s.scenarioId === selectedId);
}

export const archivedCount = (rows: readonly ScenarioItem[]): number =>
  rows.filter((s) => s.status === "archived").length;
